import { HttpClient } from './httpclient';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import { AxiosRequestConfig } from 'axios';
import {
  PartnerCampaign,
  PartnerCampaignCreateRequest,
  PartnerCampaignResponse,
} from '@/models/partnerCampaign';

/**
 * PartnerCampaignClient implements the methods to communicate with PartnerCampaign Service through
 * Management Backend service.
 */
export default class PartnerCampaignClient extends HttpClient {
  private static classInstance?: PartnerCampaignClient;

  basePath = '/api/v1/data-source';
  token = '';
  /**
   *
   * @param baseURL Base url for the client. Defaults to VUE_APP_BACKEND_BASE_URL env var.
   * @param token Authorization token to talk to adapult
   */
  private constructor(
    baseURL = process.env.VUE_APP_BACKEND_BASE_URL,
    token: string
  ) {
    super(baseURL);
    this.token = token;
    if (this.token != '') {
      this._initializeRequestInterceptor();
    }

    this._transformPayload();
  }

  /**
   *
   * @param baseURL Base url for the client. Defaults to VUE_APP_BACKEND_BASE_URL env var.
   * @param token Authorization token to talk to adapult
   * @returns
   */
  public static getInstance(
    baseURL = process.env.VUE_APP_BACKEND_BASE_URL,
    token: string
  ) {
    if (!this.classInstance) {
      this.classInstance = new PartnerCampaignClient(baseURL, token);
    }
    return this.classInstance;
  }

  /**
   * Make all requests transform from:
   * snake_case response payload -> camelCase transformation
   * camelCase request payload -> snake_case transformation
   */
  private _transformPayload = () => {
    this.instance.defaults.transformResponse = [
      (data, headers) => {
        if (data && headers) {
          if (
            headers['content-type'].includes('application/json') ||
            headers['Content-Type'].includes('application/json')
          ) {
            return camelcaseKeys(JSON.parse(data), { deep: true });
          }
        }
      },
    ];

    this.instance.defaults.transformRequest = [
      (data, headers) => {
        if (data && headers) {
          if (headers['Content-Type'].includes('application/json')) {
            headers['Content-Type'] = 'application/json';
            return JSON.stringify(snakecaseKeys(data, { deep: true }));
          }
        }
      },
    ];
  };

  /**
   * Makes all requests add the authorization token in the request IF the object
   * was constructed with a non empty token, otherwise ignored.
   */
  private _initializeRequestInterceptor = () => {
    this.instance.interceptors.request.use(
      this._handleRequest,
      this._handleError
    );
  };

  private _handleRequest = (config: AxiosRequestConfig) => {
    if (config.headers) {
      config.headers['Authorization'] = this.token;
    }

    return config;
  };

  /**
   * postPartner requests the creation of a Partner
   *
   * @param payload data to send in request's body
   * @returns promise with a http response
   */
  public postPartnerCampaign = (payload: PartnerCampaignCreateRequest) =>
    this.instance.post<PartnerCampaignResponse>(`${this.basePath}/`, payload);

  /**
   * Makes a call to the backend to retrieve a single partner campaign with an ID
   * @returns Promise to be http response
   */
  public getPartnerCampaignById = (id: number) =>
    this.instance.get<PartnerCampaign>(`${this.basePath}/${id}`);

  /**
   * editPartnerCampaign requests to edit the content of a Partner Campaign
   *
   * @param payload data to send in request's body
   * @returns promise with a http response
   */
  public editPartnerCampaign = (
    id: number,
    params: PartnerCampaignCreateRequest
  ) => this.instance.put<PartnerCampaign>(`${this.basePath}/${id}`, params);

  /**
   * Makes a call to the backend to delete a partner campaign
   * @param id The id of the partner campaign to delete
   * @returns Promise to be handled
   */
  public deletePartnerCampaign = (id: number) =>
    this.instance.delete(`${this.basePath}/${id}`);
}
